import React, { createContext, useCallback, useReducer, useMemo } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import { onSubmit } from '../api/freeform'
import { validate } from '../utils/helpers'

const initialState = {
  serviceRequests: {},
  withFileHash: {},
  caseCount: 0,
  showCurrentRequests: false,
  showCollapsedDrawer: false,
  isOpen: 'closed',
  requestType: 'new',
  disableNext: true,
  activeStep: 0,
  validated: false,
  caseId: null,
  selectedRequestType: null,
  view: 'form',
  requests: {
    new: {},
    freeform: {
      url: '/eod/cases',
      fields: {
        request_type: {
          value: 'free_form',
          required: false,
          valid: true,
        },
        type: {
          required: false,
          valid: true,
          value: 'EOD',
        },
        subject: {
          value: null,
          required: true,
          valid: true,
        },
        description: {
          value: null,
          required: true,
          valid: true,
        },
        customer_attachments: {
          value: [],
          required: false,
          valid: true,
        },
      },
    },
  }
}

export const EodDrawerContext = createContext({ mode: 'drawer', dispatch: {}, state: initialState })

export function EodDrawerProvider({ children, authenticatedFetch, maApiHost, useMutation, useQuery, mode = 'drawer' }) {
  const [state, setState] = useReducer(reducer, initialState)
  const dispatch = useMemo(() => ({}), [])

  dispatch.addFileHash = useCallback((state) => setState({ type: 'ADD_FILE_HASH', payload: state }), [])

  dispatch.updateCaseCount = useCallback((state) => setState({ type: 'UPDATE_CASE_COUNT', payload: state }), [])

  dispatch.updateServiceRequests = useCallback((state) => setState({ type: 'UPDATE_SERVICE_REQUESTS', payload: state }), [])

  dispatch.toggleView = useCallback((state) => setState({ type: 'SET_OPEN', payload: state }), [])

  dispatch.setRequestType = useCallback((state) => setState({ type: 'SET_REQUEST_TYPE', payload: state }), [])

  dispatch.setSelectedRequestType = useCallback((state) => setState({ type: 'SET_SELECTED_REQUEST_TYPE', payload: state }), [])

  dispatch.setActiveStep = useCallback((state) => setState({ type: 'SET_ACTIVE_STEP', payload: state }), [])

  dispatch.updateValidation = useCallback((state) => setState({ type: 'UPDATE_VALIDATION', payload: state }), [])

  dispatch.showCurrentRequests = useCallback((state) => setState({ type: 'SHOW_CURRENT_REQUESTS', payload: state }), [])
  dispatch.showCollapsedDrawer = useCallback((state) => setState({ type: 'SHOW_COLLAPSED_DRAWER', payload: state }), [])

  dispatch.clearValues = useCallback((state) => setState({ type: 'CLEAR_VALUES', payload: state }), [])

  dispatch.handleFieldChange = debounce(e => {
    const { name, value } = e.target
    const fields = state.requests[state.requestType].fields
    fields[name].value = value
    if (state.validated) {
      validate(fields)
      dispatch.updateValidation()
    }
  }, 200)

  dispatch.onSubmit = useCallback((setIsSubmitting) => onSubmit(state, dispatch, setIsSubmitting, maApiHost, authenticatedFetch), [state, dispatch, authenticatedFetch, maApiHost])

  dispatch.onNext = () => {
    if (state.activeStep > 0) {
      const invalidFields = validate(state.requests[state.requestType].fields)
      dispatch.updateValidation()
      if (invalidFields.length > 0) {
        return
      }
    }
    dispatch.setActiveStep(state.activeStep + 1)
  }

  dispatch.onClose = () => {
    dispatch.clearValues()
    dispatch.toggleView('closed')
  }

  dispatch.uploadFiles = useCallback((files) => {
    state.requests.freeform.fields.customer_attachments.value = files
  }, [state])

  dispatch.removeUploadedFile = useCallback((file) => {
    const files = [...state.requests.freeform.fields.customer_attachments.value]
    state.requests.freeform.fields.customer_attachments.value = files.filter(f => f.file_name !== file.name)
  }, [state])

  return <EodDrawerContext.Provider value={{ state, mode, dispatch, maApiHost, authenticatedFetch, useMutation, useQuery }}>{children}</EodDrawerContext.Provider>
}

EodDrawerProvider.propTypes = {
  children: PropTypes.node,
  authenticatedFetch: PropTypes.func,
  maApiHost: PropTypes.string,
}

function reducer(prevState, { type, payload }) {
  const newState = { ...prevState }

  switch (type) {
    case 'ADD_FILE_HASH':
      newState.withFileHash = payload
      return { ...newState }
    case 'UPDATE_CASE_COUNT':
      newState.caseCount = payload
      return { ...newState }
    case 'UPDATE_SERVICE_REQUESTS':
      newState.serviceRequests = payload
      return { ...newState }
    case 'SET_OPEN': {
      newState.isOpen = payload
      newState.activeStep = 0
      newState.disableNext = true
      newState.validated = false
      newState.requestType = 'new'
      newState.selectedRequestType = null
      return newState
    }

    case 'SET_REQUEST_TYPE': {
      newState.validated = false
      newState.selectedRequestType = null
      newState.requestType = payload
      newState.disableNext = payload === 'new'
      return newState
    }

    case 'SET_SELECTED_REQUEST_TYPE': {
      newState.selectedRequestType = payload
      newState.disableNext = payload === 'new'
      return newState
    }

    case 'UPDATE_VALIDATION': {
      newState.validated = true
      return newState
    }

    case 'SHOW_CURRENT_REQUESTS': {
      newState.showCurrentRequests = payload
      return { ...newState }
    }
    case 'SHOW_COLLAPSED_DRAWER': {
      newState.showCollapsedDrawer = payload
      return { ...newState }
    }

    case 'CLEAR_VALUES': {
      if (!newState.requests[newState?.requestType]) {
        newState.activeStep = 0
        newState.caseId = null
        newState.disableNext = true
        newState.validated = false
        newState.requestType = 'new'
        newState.selectedRequestType = null
        newState.withFileHash = {}
        return newState
      } else {
        const { fields } = newState.requests[newState.requestType]
        if (fields) {
          Object.keys(fields).forEach((field) => {
            if (field === 'request_type' || field === 'type') {
              return
            }
            fields[field].value = null
            fields[field].valid = true
          })
        }
        newState.activeStep = 0
        newState.caseId = null
        newState.disableNext = true
        newState.validated = false
        newState.requestType = 'new'
        newState.selectedRequestType = null
        newState.withFileHash = {}
        return newState
      }
    }

    default: {
      return payload
    }
  }
}

export const useEodDrawer = () => React.useContext(EodDrawerContext)
